import React, { useState } from 'react';
import i18n from '../../../i18n';
import { formatDrillUnit, formatMoney, METRICS } from '../data/metrics';
import {
  SUB_CHANNEL_NONE_KEY,
  hasSourceChannels,
  sourceMeta,
  subChannelDisplayLabel,
} from '../data/reservationSources';
import type {
  ConnectedSourceId,
  ReservationSourceId,
  SourceBreakdown,
  SourceCampaignKpis,
  SourceKpis,
  SourceSubChannelKpis,
} from '../types';
import { PairMetricLegend } from './drill/DrillPanels';
import { MiniTrend, trendStats } from './shared/Trend';

const CHANNEL_PREVIEW = 3;

function PreviewList<T>({
  items,
  itemKey,
  renderItem,
  moreLabel,
}: {
  items: T[];
  itemKey: (item: T) => string;
  renderItem: (item: T) => React.ReactNode;
  moreLabel: (hiddenCount: number) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasMore = items.length > CHANNEL_PREVIEW;
  const visible = expanded ? items : items.slice(0, CHANNEL_PREVIEW);
  const hiddenCount = items.length - CHANNEL_PREVIEW;

  return (
    <>
      {visible.map(item => (
        <React.Fragment key={itemKey(item)}>{renderItem(item)}</React.Fragment>
      ))}
      {hasMore && (
        <button
          type="button"
          className="srcmix-subtoggle"
          onClick={() => setExpanded(v => !v)}
        >
          {expanded ? 'Show less' : moreLabel(hiddenCount)}
        </button>
      )}
    </>
  );
}

function fmtNum(v: number): string {
  return v.toLocaleString('de-CH');
}

function numOrZero(v: number | null | undefined): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

export function sourceMetricValue(s: SourceKpis, metricKey?: string): number {
  switch (metricKey) {
    case 'reservations':
      return numOrZero(s.reservations);
    case 'noshow':
      return numOrZero(s.noshow);
    case 'cancel':
      return numOrZero(s.cancel);
    case 'seatTime':
      return numOrZero(s.seatTime);
    case 'sellitems':
      return numOrZero(s.sellRevenue);
    case 'revenue':
    case 'avgCheck':
    case 'revpash':
      return numOrZero(s.posMetric ?? s.posRevenue);
    case 'brainAutoPct':
      return numOrZero(s.brainAutoPct);
    case 'turns':
    case 'occupancy':
    case 'covers':
    default:
      return numOrZero(s.covers);
  }
}

/** Whether source-mix rows can render this metric as a volume figure. */
function sourceMixSupports(metricKey?: string): boolean {
  return (
    !!metricKey &&
    metricKey !== 'rejected' &&
    [
      'covers',
      'reservations',
      'noshow',
      'cancel',
      'seatTime',
      'sellitems',
      'revenue',
      'avgCheck',
      'revpash',
      'brainAutoPct',
      'turns',
      'occupancy',
    ].includes(metricKey)
  );
}

/**
 * Source mix is cover/booking mix — not every drill secondary. Rejected stays
 * on the Reservations chart/toolbar; here we keep covers beside reservations.
 */
export function resolveSourceMixKeys(
  metricKey?: string,
  secondaryKey?: string,
): { primary?: string; secondary?: string } {
  if (metricKey === 'reservations') {
    return { primary: 'reservations', secondary: 'covers' };
  }
  if (metricKey === 'covers') {
    return {
      primary: 'covers',
      secondary:
        secondaryKey && sourceMixSupports(secondaryKey)
          ? secondaryKey
          : 'reservations',
    };
  }
  return {
    primary: metricKey,
    secondary: sourceMixSupports(secondaryKey) ? secondaryKey : undefined,
  };
}

export function sourceMetricSeries(s: SourceKpis, metricKey?: string): number[] | undefined {
  switch (metricKey) {
    case 'reservations':
      return s.reservationSeries ?? s.series;
    case 'noshow':
      return s.noshowSeries;
    case 'cancel':
      return s.cancelSeries;
    case 'seatTime':
      return s.seatTimeSeries;
    case 'sellitems':
      return s.sellSeries;
    case 'revenue':
    case 'avgCheck':
    case 'revpash':
      return s.posSeries;
    case 'brainAutoPct':
      return s.brainSeries;
    case 'turns':
    case 'occupancy':
    case 'covers':
    default:
      return s.series;
  }
}

/**
 * Unrounded covers→KPI factor (fleet metric / totalCovers).
 * Period totals only: source covers × this factor = that source's slice of
 * period occupancy / turns. Never apply this factor to a single week's
 * covers — that yields ~periodOcc/nWeeks, not that week's occupancy.
 */
export function coverShareFactor(
  fleetMetric: number | undefined,
  totalCovers: number,
): number | null {
  if (fleetMetric == null || !(fleetMetric > 0) || totalCovers <= 0) return null;
  return fleetMetric / totalCovers;
}

export function coverShareDp(metricKey?: string): number {
  return metricKey === 'occupancy' ? 1 : 2;
}

/** Period contribution: source covers × (fleet metric / period covers). */
export function scaleCoverShare(
  sourceCovers: number,
  fleetMetric: number | undefined,
  totalCovers: number,
  dp: number,
): number | null {
  const factor = coverShareFactor(fleetMetric, totalCovers);
  if (factor == null) return null;
  return +(sourceCovers * factor).toFixed(dp);
}

/**
 * One bucket's occupancy/turns contribution:
 * (source covers / that week's covers) × that week's fleet occupancy/turns.
 * Across sources these sum to the chart's occupancy for that week.
 */
export function scaleCoverShareBucket(
  sourceCovers: number,
  fleetMetric: number | undefined,
  bucketCovers: number,
  dp: number,
): number {
  if (fleetMetric == null || !(fleetMetric > 0) || bucketCovers <= 0) return 0;
  return +((sourceCovers / bucketCovers) * fleetMetric).toFixed(dp);
}

/** Guest totals per series bucket — denominator for scaleCoverShareBucket. */
export function bucketCoverTotals(sources: SourceKpis[]): number[] {
  const len = Math.max(0, ...sources.map(s => s.series?.length ?? 0));
  const totals = Array.from({ length: len }, () => 0);
  for (const s of sources) {
    const series = s.series ?? [];
    for (let i = 0; i < len; i++) totals[i] += numOrZero(series[i]);
  }
  return totals;
}

/** Per-bucket occupancy/turns contributions aligned with the fleet series. */
export function scaleCoverShareSeries(
  sourceSeries: number[] | undefined,
  fleetSeries: number[] | undefined,
  coverTotals: number[],
  dp: number,
): number[] | undefined {
  if (!sourceSeries?.length || !fleetSeries?.length || !coverTotals.length) {
    return undefined;
  }
  const n = Math.min(sourceSeries.length, fleetSeries.length, coverTotals.length);
  return sourceSeries
    .slice(0, n)
    .map((covers, i) =>
      scaleCoverShareBucket(covers, fleetSeries[i], coverTotals[i], dp),
    );
}

export function isCoverShareMetric(metricKey?: string): boolean {
  return metricKey === 'turns' || metricKey === 'occupancy';
}

export function fmtSourceMetric(
  v: number,
  metricKey?: string,
  moneyUnit?: string | null,
): string {
  const n = numOrZero(v);
  if (metricKey === 'turns') {
    // Values are turns contributions (scaled from guests), not raw guests.
    return METRICS.turns.fmt(+n.toFixed(2));
  }
  if (metricKey === 'occupancy') {
    // Occupancy-point contributions (sum to period occupancy %). Not mix %.
    return formatDrillUnit(+n.toFixed(1), 'pt');
  }
  if (metricKey === 'brainAutoPct' || metricKey === 'noshow' || metricKey === 'cancel') {
    return formatDrillUnit(n, 'pct');
  }
  if (metricKey === 'seatTime') {
    return formatDrillUnit(n, 'min');
  }
  if (metricKey === 'sellitems') {
    return formatMoney(n, '€');
  }
  if (
    metricKey === 'revenue' ||
    metricKey === 'avgCheck' ||
    metricKey === 'revpash'
  ) {
    return formatMoney(n, moneyUnit || '€');
  }
  const m = metricKey ? METRICS[metricKey] : undefined;
  if (m) return m.fmt(n);
  return fmtNum(n);
}

/** Additive volume used for stacked bar + mix % (rates aren't shareable). */
/** Additive mix-bar volume for a source given the active drill metric. */
export function mixBarVolume(s: SourceKpis, metricKey?: string): number {
  switch (metricKey) {
    case 'reservations':
      return numOrZero(s.reservations);
    case 'noshow':
      return numOrZero(s.noShowCount);
    case 'cancel':
      return numOrZero(s.cancelCount);
    case 'brainAutoPct':
      return numOrZero(s.autoSeated);
    case 'sellitems':
      return numOrZero(s.sellRevenue);
    case 'revenue':
    case 'avgCheck':
    case 'revpash':
      // Rates aren't additive — bar share uses POS revenue so unmatched POS stays visible.
      return numOrZero(s.posRevenue ?? s.posMetric);
    case 'seatTime':
    case 'turns':
    case 'occupancy':
    case 'covers':
    default:
      return numOrZero(s.covers);
  }
}

/** Short noun for mix-share captions (guests / bookings / revenue / …). */
export function mixBarShareLabel(metricKey?: string): string {
  const key = (() => {
    switch (metricKey) {
      case 'reservations':
        return 'bookings';
      case 'noshow':
        return 'noShows';
      case 'cancel':
        return 'cancels';
      case 'brainAutoPct':
        return 'autoSeated';
      case 'sellitems':
        return 'sellRevenue';
      case 'revenue':
      case 'avgCheck':
      case 'revpash':
        return 'revenue';
      case 'turns':
        return 'turns';
      case 'occupancy':
        return 'occupancy';
      case 'seatTime':
      case 'covers':
      default:
        return 'guests';
    }
  })();
  return i18n.t(`performance:drillUi.shareNoun.${key}`);
}

/** Unit token for MiniTrend tips (matches fmtBucketVal). */
export function mixTrendUnit(
  metricKey?: string,
  moneyUnit?: string | null,
): string | undefined {
  if (metricKey === 'sellitems') return '€';
  if (
    metricKey === 'revenue' ||
    metricKey === 'avgCheck' ||
    metricKey === 'revpash'
  ) {
    return moneyUnit || '€';
  }
  if (
    metricKey === 'brainAutoPct' ||
    metricKey === 'noshow' ||
    metricKey === 'cancel'
  ) {
    return '%';
  }
  if (metricKey === 'seatTime') return 'm';
  // Guest-share metrics must be scaled first; never tip raw guests as "36×" / "36%".
  if (metricKey === 'turns') return '×';
  if (metricKey === 'occupancy') return 'pt';
  const u = metricKey ? METRICS[metricKey]?.unit : undefined;
  if (u === 'min') return 'm';
  if (u === '%' || u === '€') return u;
  return undefined;
}

function mixBarSecondary(
  s: SourceKpis,
  totals: { covers: number; reservations: number },
  metricKey?: string,
): { share: number; label: string } {
  if (metricKey === 'reservations') {
    return { share: pctOf(s.covers, totals.covers), label: 'guests' };
  }
  return {
    share: pctOf(s.reservations, totals.reservations),
    label: 'reservations',
  };
}

type MixVolume = {
  covers: number;
  reservations: number;
};

export function pctOf(value: number, total: number): number {
  return total ? +((value / total) * 100).toFixed(1) : 0;
}

function mixShares(
  row: MixVolume,
  parent: MixVolume,
): { coverShare: number; reservationShare: number } {
  return {
    coverShare: pctOf(row.covers, parent.covers),
    reservationShare: pctOf(row.reservations, parent.reservations),
  };
}

function metricShortLabel(metricKey?: string): string {
  switch (metricKey) {
    case 'reservations':
      return 'reservations';
    case 'rejected':
      return 'rejected';
    case 'noshow':
      return 'no-show rate';
    case 'cancel':
      return 'cancellation rate';
    case 'seatTime':
      return 'seat time';
    case 'sellitems':
      return 'sell items';
    case 'revenue':
      return 'revenue';
    case 'avgCheck':
      return 'avg spend per guest';
    case 'revpash':
      return 'revpash';
    case 'brainAutoPct':
      return 'brain seating';
    case 'turns':
      return 'turns';
    case 'occupancy':
      return 'occupancy';
    case 'covers':
      return 'guests';
    default: {
      const fromMetrics = metricKey ? METRICS[metricKey]?.short : undefined;
      return fromMetrics ? fromMetrics.toLowerCase() : 'covers';
    }
  }
}

function MetricFigLine({
  value,
  metricKey,
  moneyUnit,
  paired,
}: {
  value: number;
  metricKey?: string;
  moneyUnit?: string | null;
  paired?: 'secondary';
}) {
  return (
    <span
      className={`srcmix-fig-line${paired === 'secondary' ? ' srcmix-fig-line-2' : ''}`}
    >
      <span className="srcmix-fig-val">
        {fmtSourceMetric(value, metricKey, moneyUnit)}
      </span>
      <span className="srcmix-fig-metric-lab">{metricShortLabel(metricKey)}</span>
    </span>
  );
}

function VolLine({
  formatted,
  label,
  paired,
}: {
  formatted: string;
  label: string;
  paired?: boolean;
}) {
  return (
    <span className={`srcmix-vol-line${paired ? ' srcmix-vol-line-2' : ''}`}>
      <span className="srcmix-covers">{formatted}</span>
      <span className="srcmix-vol-lab">{label}</span>
    </span>
  );
}

function ShareLine({
  share,
  label,
  paired,
}: {
  share: number;
  label: string;
  paired?: boolean;
}) {
  return (
    <span className={`srcmix-share-line${paired ? ' srcmix-share-line-2' : ''}`}>
      <span className="srcmix-share">{share}%</span>
      <span className="srcmix-share-lab">{label}</span>
    </span>
  );
}

// Period totals for guests, reservations, and both mix shares — aligned so the
// percentages always match the volume figures on the same row.
function MixRowVol({ row, parent }: { row: MixVolume; parent: MixVolume }) {
  const shares = mixShares(row, parent);
  return (
    <div className="srcmix-vol">
      <VolLine formatted={fmtNum(row.covers)} label="guests" />
      <VolLine formatted={fmtNum(row.reservations)} label="reservations" paired />
      <ShareLine share={shares.coverShare} label="guest share" />
      <ShareLine share={shares.reservationShare} label="reservation share" paired />
    </div>
  );
}

function MixShareLines({
  primaryShare,
  primaryLabel,
  secondary,
}: {
  primaryShare: number;
  primaryLabel: string;
  secondary: { share: number; label: string };
}) {
  return (
    <div className="srcmix-fig-item">
      <span className="srcmix-fig-lab">Mix share</span>
      <span className="srcmix-fig-num">
        {primaryShare}% {primaryLabel}
      </span>
      <span className="srcmix-fig-num srcmix-fig-num-2">
        {secondary.share}% {secondary.label}
      </span>
    </div>
  );
}

// Headline figures for one source row - each number gets an explicit caption so
// latest-bucket guests, mix share, momentum, and period total read distinctly.
function SourceFigures({
  s,
  mode,
  st,
  labels,
  aggregationLabel,
  periodLabel,
  comparable = true,
  metricKey,
  secondaryKey,
  moneyUnit,
  metricValue,
  metricSeries,
  bucketCovers,
  totalReservations,
  totalCovers,
  mixShare,
  mixShareLabel,
}: {
  s: SourceKpis;
  mode: 'live' | 'performance';
  st: ReturnType<typeof trendStats> | null;
  labels: string[];
  aggregationLabel?: string;
  periodLabel?: string;
  comparable?: boolean;
  metricKey?: string;
  secondaryKey?: string;
  moneyUnit?: string | null;
  /** Fleet-level metric (turns × / occupancy %) used to scale cover contributions. */
  metricValue?: number;
  /** Per-bucket fleet occupancy / turns, aligned with source cover series. */
  metricSeries?: number[];
  /** Guest totals per bucket — denominator for weekly occupancy/turns pts. */
  bucketCovers?: number[];
  totalReservations: number;
  totalCovers: number;
  mixShare: number;
  mixShareLabel: string;
}) {
  const mixKeys = resolveSourceMixKeys(metricKey, secondaryKey);
  const dp = coverShareDp(mixKeys.primary);
  const periodScaled = isCoverShareMetric(mixKeys.primary)
    ? scaleCoverShare(
        sourceMetricValue(s, mixKeys.primary),
        metricValue,
        totalCovers,
        dp,
      )
    : null;
  const periodPrimary = periodScaled ?? sourceMetricValue(s, mixKeys.primary);
  const periodSecondary = mixKeys.secondary
    ? sourceMetricValue(s, mixKeys.secondary)
    : undefined;
  const secondarySeries = mixKeys.secondary
    ? sourceMetricSeries(s, mixKeys.secondary)
    : undefined;
  const primaryMixLabel = mixShareLabel;
  const secondaryMix = mixBarSecondary(
    s,
    { covers: totalCovers, reservations: totalReservations },
    metricKey,
  );
  const ln = labels.length;
  const hasCtx = mode === 'performance' && st?.hasSeries;
  const rawSeries = sourceMetricSeries(s, mixKeys.primary);
  const contribSeries =
    isCoverShareMetric(mixKeys.primary) && hasCtx
      ? scaleCoverShareSeries(rawSeries, metricSeries, bucketCovers ?? [], dp)
      : undefined;
  // Latest bucket uses that week's fleet occupancy/turns × that week's mix,
  // never the period factor on weekly guest counts.
  const latestPrimary = contribSeries?.length
    ? contribSeries[contribSeries.length - 1]
    : hasCtx && !isCoverShareMetric(mixKeys.primary)
      ? st!.latest
      : periodPrimary;
  const latestSecondary =
    hasCtx && secondarySeries?.length
      ? secondarySeries[secondarySeries.length - 1]
      : periodSecondary;
  const latestLabel = ln ? labels[ln - 1] : '';
  const prevLabel = ln > 1 ? labels[ln - 2] : '';
  const prevContrib =
    contribSeries && contribSeries.length > 1
      ? contribSeries[contribSeries.length - 2]
      : undefined;
  const occupancyDelta =
    mixKeys.primary === 'occupancy' && prevContrib != null
      ? +(latestPrimary - prevContrib).toFixed(1)
      : null;
  const deltaVal =
    occupancyDelta != null
      ? occupancyDelta
      : hasCtx
        ? st!.delta
        : s.deltaPct;
  const deltaUnit =
    occupancyDelta != null ? 'pt' : '%';
  const rangeLabel =
    periodLabel ??
    (ln > 1 ? `${labels[0]} – ${labels[ln - 1]}` : labels[0] ?? '');
  const periodLab =
    metricKey === 'noshow' ||
    metricKey === 'cancel' ||
    metricKey === 'brainAutoPct' ||
    metricKey === 'seatTime' ||
    metricKey === 'avgCheck' ||
    metricKey === 'revpash'
      ? 'Period'
      : 'Period total';

  if (hasCtx && comparable) {
    return (
      <div className="srcmix-fig">
        <div className="srcmix-fig-now">
          <MetricFigLine
            value={latestPrimary}
            metricKey={mixKeys.primary}
            moneyUnit={moneyUnit}
          />
          {latestSecondary != null && mixKeys.secondary && (
            <MetricFigLine
              value={latestSecondary}
              metricKey={mixKeys.secondary}
              moneyUnit={moneyUnit}
              paired="secondary"
            />
          )}
          <span className="srcmix-fig-when">
            Latest {aggregationLabel ?? 'period'}
          </span>
          <span className="srcmix-fig-period">{latestLabel}</span>
        </div>
        <div className="srcmix-fig-meta">
          <MixShareLines
            primaryShare={mixShare}
            primaryLabel={primaryMixLabel}
            secondary={secondaryMix}
          />
          {deltaVal != null && (
            <div className="srcmix-fig-item">
              <span className="srcmix-fig-lab">
                vs {prevLabel || `prior ${aggregationLabel ?? 'period'}`}
              </span>
              <span
                className="srcmix-fig-num srcmix-fig-delta"
                style={{
                  color:
                    deltaVal >= 0 ? 'var(--st-steady)' : 'var(--st-alert)',
                }}
              >
                {deltaVal >= 0 ? '+' : ''}
                {deltaVal}{deltaUnit}
              </span>
            </div>
          )}
        </div>
        <div className="srcmix-fig-total">
          <span className="srcmix-fig-lab">{periodLab}</span>
          <div className="srcmix-fig-total-lines">
            <MetricFigLine
              value={periodPrimary}
              metricKey={mixKeys.primary}
              moneyUnit={moneyUnit}
            />
            {periodSecondary != null && mixKeys.secondary && (
              <MetricFigLine
                value={periodSecondary}
                metricKey={mixKeys.secondary}
                moneyUnit={moneyUnit}
                paired="secondary"
              />
            )}
          </div>
          <span className="srcmix-fig-period">{rangeLabel}</span>
        </div>
      </div>
    );
  }

  if (hasCtx && !comparable) {
    return (
      <div className="srcmix-fig">
        <div className="srcmix-fig-now">
          <MetricFigLine
            value={periodPrimary}
            metricKey={mixKeys.primary}
            moneyUnit={moneyUnit}
          />
          {periodSecondary != null && mixKeys.secondary && (
            <MetricFigLine
              value={periodSecondary}
              metricKey={mixKeys.secondary}
              moneyUnit={moneyUnit}
              paired="secondary"
            />
          )}
          <span className="srcmix-fig-when">{periodLab}</span>
          <span className="srcmix-fig-period">{rangeLabel}</span>
        </div>
        <div className="srcmix-fig-meta">
          <MixShareLines
            primaryShare={mixShare}
            primaryLabel={primaryMixLabel}
            secondary={secondaryMix}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="srcmix-fig">
      <div className="srcmix-fig-now">
        <MetricFigLine
          value={periodPrimary}
          metricKey={mixKeys.primary}
          moneyUnit={moneyUnit}
        />
        {periodSecondary != null && mixKeys.secondary && (
          <MetricFigLine
            value={periodSecondary}
            metricKey={mixKeys.secondary}
            moneyUnit={moneyUnit}
            paired="secondary"
          />
        )}
        {mode === 'live' && (
          <span className="srcmix-fig-when">Today</span>
        )}
      </div>
      <div className="srcmix-fig-meta">
        <MixShareLines
          primaryShare={mixShare}
          primaryLabel={primaryMixLabel}
          secondary={secondaryMix}
        />
        {mode === 'performance' && comparable && deltaVal != null && (
          <div className="srcmix-fig-item">
            <span className="srcmix-fig-lab">
              vs prior {aggregationLabel ?? 'period'}
            </span>
            <span
              className="srcmix-fig-num srcmix-fig-delta"
              style={{
                color: deltaVal >= 0 ? 'var(--st-steady)' : 'var(--st-alert)',
              }}
            >
              {deltaVal >= 0 ? '+' : ''}
              {deltaVal}{deltaUnit}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// Per-source quality chips. Sell-items only shows when aleno Pay is connected
// (or revenue exists), matching how the rest of the dashboard gates sell-items.
function SourceStats({
  s,
  mode,
  showSell,
}: {
  s: SourceKpis;
  mode: 'live' | 'performance';
  showSell: boolean;
}) {
  return (
    <div className="srcmix-stats">
      {mode === 'live' && s.upcoming > 0 && (
        <span className="srcmix-stat" title="Incoming guests · next 2h">
          +{fmtNum(s.upcoming)} in
        </span>
      )}
      <span className="srcmix-stat" title="No-show rate">
        {s.noshow}% no-show
      </span>
      <span className="srcmix-stat" title="Cancellation rate">
        {s.cancel}% cancel
      </span>
      {s.seatTime > 0 && (
        <span className="srcmix-stat" title="Average seating time">
          {s.seatTime}m seat
        </span>
      )}
      {showSell && s.sellRevenue > 0 && (
        <span className="srcmix-stat" title="Sell-items revenue (aleno Pay)">
          €{fmtNum(Math.round(s.sellRevenue))}
        </span>
      )}
    </div>
  );
}

function SourceCampaignRows({
  campaigns,
  color,
  parent,
}: {
  campaigns: SourceCampaignKpis[];
  color: string;
  parent: MixVolume;
}) {
  return (
    <div className="srcmix-camplist">
      <div className="srcmix-camphead">Campaigns</div>
      <PreviewList
        items={campaigns}
        itemKey={camp => camp.key}
        moreLabel={hidden =>
          `Show ${fmtNum(hidden)} more campaign${hidden === 1 ? '' : 's'}`
        }
        renderItem={camp => (
          <div className="srcmix-camprow">
            <div className="srcmix-name">
              <span
                className="srcmix-dot"
                style={{ background: color, opacity: 0.3 }}
              />
              <span title={camp.label}>{camp.label}</span>
            </div>
            <MixRowVol row={camp} parent={parent} />
          </div>
        )}
      />
    </div>
  );
}

function SourceSubChannelRows({
  parent,
  subChannels,
}: {
  parent: SourceKpis;
  subChannels: SourceSubChannelKpis[];
}) {
  const parentMeta = sourceMeta(parent.source);
  const parentVolume: MixVolume = {
    covers: parent.covers,
    reservations: parent.reservations,
  };

  return (
    <div className="srcmix-sublist">
      <div className="srcmix-subhead">
        Channel breakdown within {parentMeta.label}
      </div>
      <PreviewList
        items={subChannels}
        itemKey={sub => sub.key}
        moreLabel={hidden =>
          `Show ${fmtNum(hidden)} more channel${hidden === 1 ? '' : 's'}`
        }
        renderItem={sub => {
          const subLabel = subChannelDisplayLabel(parent.source, sub);
          const subVolume: MixVolume = {
            covers: sub.covers,
            reservations: sub.reservations,
          };
          return (
            <div className="srcmix-subgroup">
              <div className="srcmix-subrow">
                <div className="srcmix-name">
                  <span
                    className="srcmix-dot"
                    style={{ background: parentMeta.color, opacity: 0.45 }}
                  />
                  <span
                    title={
                      sub.key === SUB_CHANNEL_NONE_KEY
                        ? 'Widget reservation without utm.source tracking'
                        : subLabel
                    }
                  >
                    {subLabel}
                  </span>
                </div>
                <MixRowVol row={subVolume} parent={parentVolume} />
              </div>
              {sub.campaigns && sub.campaigns.length > 0 && (
                <SourceCampaignRows
                  campaigns={sub.campaigns}
                  color={parentMeta.color}
                  parent={subVolume}
                />
              )}
            </div>
          );
        }}
      />
    </div>
  );
}

// Source comparison module shown right under the service ribbon on both tabs.
export function SourceMixPanel({
  breakdown,
  mode,
  connected,
  bucketLabels,
  aggregationLabel,
  periodLabel,
  comparable = true,
  metricKey,
  secondaryKey,
  moneyUnit,
  metricValue,
  metricSeries: fleetMetricSeries,
  embedded = false,
}: {
  breakdown?: SourceBreakdown;
  mode: 'live' | 'performance';
  connected?: ConnectedSourceId[];
  bucketLabels?: string[];
  aggregationLabel?: string;
  periodLabel?: string;
  comparable?: boolean;
  metricKey?: string;
  secondaryKey?: string;
  /** POS / money display unit (CHF, €, …). */
  moneyUnit?: string | null;
  /** Fleet metric value (e.g. turns ×) for scaling cover-based series. */
  metricValue?: number;
  /** Per-bucket fleet occupancy / turns, aligned with source cover series. */
  metricSeries?: number[];
  /** Nest inside a DrillSection Advanced block (hide outer title). */
  embedded?: boolean;
}) {
  if (!breakdown || !breakdown.sources.length || !breakdown.totalCovers) {
    return null;
  }

  const showSell =
    !!connected?.includes('alenopay') ||
    breakdown.sources.some(s => s.sellRevenue > 0);
  const resolvedMoneyUnit =
    moneyUnit || breakdown.posUnit || breakdown.posCurrency || null;
  const isPosMetric =
    metricKey === 'revenue' ||
    metricKey === 'avgCheck' ||
    metricKey === 'revpash';

  const barLabel = mixBarShareLabel(metricKey);
  const mixKeys = resolveSourceMixKeys(metricKey, secondaryKey);
  const metricLabel = metricShortLabel(mixKeys.primary ?? metricKey);
  const primaryShort =
    (mixKeys.primary && METRICS[mixKeys.primary]?.short) || undefined;
  const secondaryShort =
    (mixKeys.secondary && METRICS[mixKeys.secondary]?.short) || undefined;
  const sources = [...breakdown.sources].sort(
    (a, b) => mixBarVolume(b, metricKey) - mixBarVolume(a, metricKey),
  );
  const totalReservations = sources.reduce((a, s) => a + s.reservations, 0);
  const totalCovers = breakdown.totalCovers;
  const totalMixVolume = sources.reduce(
    (a, s) => a + mixBarVolume(s, metricKey),
    0,
  );
  // Fall back to guest share when the metric's additive base is empty
  // (e.g. zero no-shows in the period).
  const useCoverBar = totalMixVolume <= 0;
  const coverTotals = bucketCoverTotals(sources);
  const [openSource, setOpenSource] = useState<ReservationSourceId | null>(
    null,
  );

  const hasCtx = mode === 'performance' && !!bucketLabels?.length;
  const pairSubtitle =
    primaryShort && secondaryShort
      ? `${primaryShort} vs ${secondaryShort}`
      : metricLabel;
  const subtitle =
    mode === 'live'
      ? "Today's covers and reservations by origin · mix share uses the same totals"
      : hasCtx && comparable
        ? `Latest ${aggregationLabel ?? 'period'} ${pairSubtitle} by origin · ${barLabel} mix share`
        : hasCtx
          ? `Period ${pairSubtitle} by origin · ${periodLabel ?? 'selected range'}`
          : `Period ${pairSubtitle} by origin · ${barLabel} mix share`;

  return (
    <section className={`srcmix${embedded ? ' srcmix-embedded' : ''}`}>
      {!embedded && (
        <div className="sec-head">
          <h2>Reservation source mix</h2>
          <span className="sh-meta">{subtitle}</span>
          {primaryShort && secondaryShort ? (
            <PairMetricLegend
              primaryLabel={primaryShort}
              secondaryLabel={secondaryShort}
            />
          ) : null}
          <span
            className="prov-note"
            title={
              isPosMetric && breakdown.posMixedCurrency
                ? 'POS baskets span more than one currency in this scope. By-source POS totals are omitted.'
                : isPosMetric && breakdown.posLinked
                  ? 'POS revenue joined to reservation origin via reservation.basketid = basket._id. Till tickets with no matching reservation show as “POS without a reservation”.'
                  : isPosMetric && breakdown.posAllocated
                    ? 'Legacy guest-share allocation (no basket→reservation join).'
                    : 'Derived from reservation origin. Widget expands into utm.source channels (e.g. Google web vs unattributed).'
            }
          >
            {isPosMetric && breakdown.posMixedCurrency
              ? 'Mixed POS currency'
              : isPosMetric && breakdown.posLinked
                ? 'From basket→reservation'
                : isPosMetric && breakdown.posAllocated
                  ? 'POS by guest share'
                  : 'From reservations'}
          </span>
        </div>
      )}

      <div className="ol-card">
        <div
          className="srcmix-bar"
          title={`Share of ${useCoverBar ? 'guests' : barLabel} by source`}
        >
          {sources.map(s => {
            const m = sourceMeta(s.source);
            const vol = useCoverBar ? s.covers : mixBarVolume(s, metricKey);
            const total = useCoverBar ? totalCovers : totalMixVolume;
            const share = pctOf(vol, total);
            const tipVal =
              metricKey === 'sellitems' ||
              metricKey === 'revenue' ||
              metricKey === 'avgCheck' ||
              metricKey === 'revpash'
                ? fmtSourceMetric(
                    vol,
                    metricKey === 'avgCheck' || metricKey === 'revpash'
                      ? 'revenue'
                      : metricKey,
                    resolvedMoneyUnit,
                  )
                : fmtNum(vol);
            return (
              <span
                key={s.source}
                className="srcmix-seg"
                style={{
                  width: `${share}%`,
                  background: m.color,
                }}
                title={`${m.label} · ${tipVal} ${useCoverBar ? 'guests' : barLabel} (${share}%)`}
              />
            );
          })}
        </div>

        <div className="srcmix-list">
          {sources.map(s => {
            const m = sourceMeta(s.source);
            const hasSubChannels =
              hasSourceChannels(s.source) && !!s.subChannels?.length;
            const subOpen = hasSubChannels && openSource === s.source;
            const rawSeries = sourceMetricSeries(
              s,
              mixKeys.primary ?? metricKey,
            );
            const primaryKey = mixKeys.primary ?? metricKey;
            const trendSeries = isCoverShareMetric(primaryKey)
              ? scaleCoverShareSeries(
                  rawSeries,
                  fleetMetricSeries,
                  coverTotals,
                  coverShareDp(primaryKey),
                )
              : rawSeries;
            // Trend deltas stay on guest units (same shape); tips use scaled series.
            const st =
              mode === 'performance' && rawSeries
                ? trendStats(rawSeries)
                : null;
            const secondarySeries = mixKeys.secondary
              ? sourceMetricSeries(s, mixKeys.secondary)
              : undefined;
            const labels = bucketLabels ?? [];
            const mixVol = useCoverBar ? s.covers : mixBarVolume(s, metricKey);
            const mixTotal = useCoverBar ? totalCovers : totalMixVolume;
            const mixShare = pctOf(mixVol, mixTotal);
            const trendAria = secondaryShort
              ? `${m.label} ${primaryShort ?? metricLabel} and ${secondaryShort} per ${aggregationLabel ?? 'period'}`
              : `${m.label} ${metricLabel} per ${aggregationLabel ?? 'period'}`;
            return (
              <div
                key={s.source}
                className={`srcmix-group${subOpen ? ' open' : ''}`}
              >
                <div
                  className={`srcmix-row${st?.hasSeries ? ' srcmix-row-ctx' : ''}`}
                >
                  <div
                    className={`srcmix-name${hasSubChannels ? ' clickable' : ''}`}
                    onClick={
                      hasSubChannels
                        ? () =>
                            setOpenSource(v =>
                              v === s.source ? null : s.source,
                            )
                        : undefined
                    }
                    role={hasSubChannels ? 'button' : undefined}
                    aria-expanded={hasSubChannels ? subOpen : undefined}
                    title={
                      hasSubChannels
                        ? subOpen
                          ? `Hide ${m.label} breakdown`
                          : `Show ${m.label} breakdown`
                        : undefined
                    }
                  >
                    <span
                      className="srcmix-dot"
                      style={{ background: m.color }}
                    />
                    <b>{m.label}</b>
                  </div>
                  <SourceFigures
                    s={s}
                    mode={mode}
                    st={st}
                    labels={labels}
                    aggregationLabel={aggregationLabel}
                    periodLabel={periodLabel}
                    comparable={comparable}
                    metricKey={metricKey}
                    secondaryKey={secondaryKey}
                    moneyUnit={resolvedMoneyUnit}
                    metricValue={metricValue}
                    metricSeries={fleetMetricSeries}
                    bucketCovers={coverTotals}
                    totalReservations={totalReservations}
                    totalCovers={totalCovers}
                    mixShare={mixShare}
                    mixShareLabel={useCoverBar ? 'guests' : barLabel}
                  />
                  {st?.hasSeries && (
                    <div className="srcmix-trend" aria-label={trendAria}>
                      <MiniTrend
                        values={trendSeries ?? []}
                        secondaryValues={secondarySeries}
                        labels={labels}
                        unit={
                          // Never tip raw guests as "36×" / occupancy % — only after scaling.
                          isCoverShareMetric(mixKeys.primary ?? metricKey) &&
                          !trendSeries
                            ? undefined
                            : mixTrendUnit(
                                mixKeys.primary ?? metricKey,
                                resolvedMoneyUnit,
                              )
                        }
                        secondaryUnit={mixTrendUnit(
                          mixKeys.secondary,
                          resolvedMoneyUnit,
                        )}
                        primaryLabel={primaryShort}
                        secondaryLabel={secondaryShort}
                        compact
                      />
                    </div>
                  )}
                  <SourceStats s={s} mode={mode} showSell={showSell} />
                </div>
                {subOpen && (
                  <SourceSubChannelRows
                    parent={s}
                    subChannels={s.subChannels!}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
